import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  adminCreatePromo,
  adminCreateBundle,
  adminDuplicateProduct,
} from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Gift, Package, Tag, Copy } from "lucide-react";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

/**
 * "Buy X Get Y Free" BOGO promo dialog.
 *
 * Attaches a promo as metadata on the EXISTING product row — we never
 * duplicate a product.  The backend stores a `[BOGO:buy:free:start:end]`
 * tag in the product's `blurb` field; the catalogue list/shop parse it
 * back so the gift badge and cart auto-add can react.
 */
export function PromoDialog({ open, onOpenChange, product, callerUserRecordId, onSaved }) {
  const existing = product?.promo || null;
  const [buyQty, setBuyQty] = useState(3);
  const [freeQty, setFreeQty] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (open) {
      setBuyQty(existing?.buy_qty || 3);
      setFreeQty(existing?.free_qty || 1);
      setStartDate(existing?.start_date || "");
      setEndDate(existing?.end_date || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  if (!product) return null;

  const label = `Buy ${buyQty} Get ${freeQty} Free`;

  async function save() {
    if (buyQty <= 0 || freeQty <= 0) {
      toast.error("Quantities must be > 0");
      return;
    }
    setSaving(true);
    try {
      await adminCreatePromo({
        callerUserRecordId,
        productRecordId: product.id,
        buyQty,
        freeQty,
        startDate,
        endDate,
      });
      toast.success(`${label} promo saved`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save promo");
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    setClearing(true);
    try {
      await adminCreatePromo({
        callerUserRecordId,
        productRecordId: product.id,
        buyQty: 0,
        freeQty: 0,
      });
      toast.success("Promo removed");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear promo");
    } finally {
      setClearing(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="promo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift size={16} className="text-rose-500" />
            {existing ? "Edit Promo for" : "Add Promo for"}
          </DialogTitle>
          <div className="text-base font-bold text-zinc-900 truncate">
            {product.name}
          </div>
          {existing && (
            <div className="mt-1 text-xs text-rose-700 inline-flex items-center gap-1">
              <Gift size={11} /> Current: <b>{existing.label}</b>
              {existing.start_date && (
                <span className="text-zinc-500">
                  · {existing.start_date}
                  {existing.end_date ? ` → ${existing.end_date}` : ""}
                </span>
              )}
            </div>
          )}
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Buy Quantity</Label>
              <Input
                type="number"
                min="1"
                value={buyQty}
                onChange={(e) => setBuyQty(Math.max(1, Number(e.target.value) || 1))}
                className="mt-1 h-10"
                data-testid="promo-buy-qty"
              />
            </div>
            <div>
              <Label className="text-xs">Free Quantity</Label>
              <Input
                type="number"
                min="1"
                value={freeQty}
                onChange={(e) => setFreeQty(Math.max(1, Number(e.target.value) || 1))}
                className="mt-1 h-10"
                data-testid="promo-free-qty"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 h-10"
              />
            </div>
          </div>
          <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-3 text-xs space-y-1.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-amber-700">
              Saved on the product (no duplicate row)
            </div>
            <div className="font-semibold text-rose-700 flex items-center gap-1">
              <Gift size={12} /> {label}
            </div>
            <div className="text-amber-700 flex items-center gap-1">
              <Tag size={11} /> Label: <b>{label}</b>
            </div>
            <div className="text-amber-700">
              Banner: Buy {buyQty} {product.name} and get {freeQty} FREE.
            </div>
            <div className="text-amber-700">
              Cart will automatically add {freeQty} free unit
              {freeQty > 1 ? "s" : ""} for every {buyQty} paid.
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            {existing && (
              <Button
                variant="outline"
                onClick={clear}
                disabled={clearing || saving}
                className="border-rose-200 text-rose-700 hover:bg-rose-50 mr-auto"
                data-testid="promo-clear"
              >
                {clearing ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  "Remove Promo"
                )}
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || clearing}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="promo-save"
            >
              {saving ? (
                <Loader2 className="animate-spin" size={14} />
              ) : existing ? (
                "Update Promo"
              ) : (
                "Save Promo"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Bundle creator — combines 2+ selected products into a new product.
 */
export function BundleDialog({ open, onOpenChange, products, callerUserRecordId, onSaved }) {
  const [name, setName] = useState("");
  const [discountPct, setDiscountPct] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && products?.length) {
      // Auto-suggest a name
      setName(
        products.length === 2
          ? `${products[0].name} + ${products[1].name}`
          : `${products.length}-Product Bundle`
      );
      setDiscountPct(0);
    }
  }, [open, products]);

  if (!products || products.length < 2) return null;

  const totalPrice = products.reduce((s, p) => s + Number(p.price || 0), 0);
  const finalPrice =
    discountPct > 0 ? totalPrice * (1 - discountPct / 100) : totalPrice;
  const totalWeight = products.reduce((s, p) => s + Number(p.weight || 0), 0);

  async function save() {
    if (!name.trim()) {
      toast.error("Bundle name required");
      return;
    }
    setSaving(true);
    try {
      await adminCreateBundle({
        callerUserRecordId,
        productRecordIds: products.map((p) => p.id),
        name: name.trim(),
        discountPct,
      });
      toast.success(`Bundle "${name.trim()}" created`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create bundle");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bundle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={16} className="text-violet-600" />
            Create Bundle ({products.length} products)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs">Bundle name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-10"
              data-testid="bundle-name"
            />
          </div>
          <div>
            <Label className="text-xs">Discount %</Label>
            <Input
              type="number"
              min="0"
              max="90"
              value={discountPct}
              onChange={(e) =>
                setDiscountPct(Math.max(0, Math.min(90, Number(e.target.value) || 0)))
              }
              className="mt-1 h-10"
              data-testid="bundle-discount"
            />
          </div>
          <ul className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 max-h-[180px] overflow-y-auto">
            {products.map((p) => (
              <li key={p.id} className="px-3 py-2 flex items-center gap-2">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt=""
                    className="w-8 h-8 rounded-lg object-cover bg-zinc-100"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-zinc-100" />
                )}
                <span className="flex-1 text-sm truncate">{p.name}</span>
                <span className="text-xs font-semibold tabular-nums">
                  {fmtUSD(p.price)}
                </span>
              </li>
            ))}
          </ul>
          <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span>MSRP sum</span>
              <span className="font-semibold">{fmtUSD(totalPrice)}</span>
            </div>
            {discountPct > 0 && (
              <div className="flex justify-between text-rose-700">
                <span>− {discountPct}% discount</span>
                <span className="font-semibold">
                  −{fmtUSD(totalPrice - finalPrice)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-base text-violet-700 font-bold pt-1 border-t border-violet-200">
              <span>Bundle price</span>
              <span>{fmtUSD(finalPrice)}</span>
            </div>
            <div className="text-zinc-500 text-[10px]">
              Combined weight: {totalWeight} oz
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="bundle-save"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : "Create Bundle"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Simple confirm dialog for Duplicate action.
 */
export function DuplicateDialog({ open, onOpenChange, product, callerUserRecordId, onSaved }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && product) setName(`${product.name} (copy)`);
  }, [open, product]);

  if (!product) return null;

  async function save() {
    setSaving(true);
    try {
      await adminDuplicateProduct({
        callerUserRecordId,
        productRecordId: product.id,
        name: name.trim(),
      });
      toast.success("Product duplicated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not duplicate");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy size={16} className="text-emerald-600" />
            Duplicate product
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div>
            <Label className="text-xs">New product name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-10"
              data-testid="duplicate-name"
            />
          </div>
          <p className="text-xs text-zinc-500">
            The new product will be created <b>inactive</b> so you can review
            and adjust pricing before flipping it on.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !name.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="duplicate-save"
            >
              {saving ? <Loader2 className="animate-spin" size={14} /> : "Duplicate"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
